import type { ListDeclarationsParams } from "../../api/declarationsApi"
import type { DeclarationSortBy, DeclarationSortDir } from "../../api/types"

/**
 * THE OVERVIEW'S OWN FILTER/SORT PARAMS (TUR-1211) — the single source of
 * truth for which URL keys `DeclarationListV2` reads as its server-side
 * filters/sort, shared with everything that must carry the SAME list a
 * reviewer was looking at across a navigation: `DeclarationsModule`'s row
 * click into review, the review page's own list read, and every "next
 * declaration" jump out of the review workspace (Überspringen, the
 * post-approve jump, "other pending"). Without this, the review route fetched
 * the list with no params — the server's own no-sort default is `ORDER BY
 * id` (insertion order), so skip order had nothing to do with what the
 * reviewer actually saw.
 *
 * `supplier_id` (singular) is the read-only deep-link alias `supplierIds`
 * falls back to (see `listParamsFromSearch` below) — it travels alongside
 * `supplierIds` rather than being folded into it, so a link built from these
 * keys round-trips exactly the params the URL already carries.
 */
export const DECLARATION_LIST_PARAM_KEYS = [
  "lifecycle",
  "supplier",
  "quality",
  "supplierIds",
  "supplier_id",
  "docTypes",
  "frameworks",
  "held",
  "sortBy",
  "sortDir",
] as const

/** The backend sort keys the list endpoint accepts, in the sort menu's own
 *  display order. Runtime companion to the `DeclarationSortBy` type (which
 *  vanishes at compile time) — `sanitizedSortBy` below validates against it. */
const SORT_BY_VALUES: readonly DeclarationSortBy[] = [
  "supplier",
  "doc_type",
  "framework",
  "received_at",
  "status",
]
const SORT_BY_VALUE_SET = new Set<string>(SORT_BY_VALUES)

/** The one sort key backed by a date rather than a string — its direction
 *  toggle reads "Newest/Oldest first" instead of "A-Z/Z-A", and it is the
 *  wire default when no sort is chosen at all (see `listParamsFromSearch`). */
export const DATE_SORT_KEY: DeclarationSortBy = "received_at"

/** Validates a `sortBy` URL param against the known vocab (`SORT_BY_VALUES`)
 *  — an invalid value (a hand-typed or stale deep link, e.g. a renamed/
 *  removed sort key) reads exactly like an ABSENT one rather than being sent
 *  to the server: the endpoint 422s on any value outside its own enum, which
 *  — with no error surface on this query — rendered a silently empty inbox
 *  instead of the honest default ordering. */
export function sanitizedSortBy(
  raw: string | null,
): DeclarationSortBy | undefined {
  return raw && SORT_BY_VALUE_SET.has(raw)
    ? (raw as DeclarationSortBy)
    : undefined
}

/** Same validation, for `sortDir` — the vocab here is just `asc | desc`. */
export function sanitizedSortDir(
  raw: string | null,
): DeclarationSortDir | undefined {
  return raw === "asc" || raw === "desc" ? raw : undefined
}

/** A CSV facet param (`lifecycle`/`docTypes`/`frameworks`) — split, drop any
 *  empty entry (a trailing/doubled comma), and rejoin, so the wire value is
 *  either a clean CSV or entirely absent (never `""`, which the endpoint
 *  would read as an active-but-empty filter). */
function csvParam(params: URLSearchParams, key: string): string | undefined {
  const values = params.get(key)?.split(",").filter(Boolean) ?? []
  return values.length > 0 ? values.join(",") : undefined
}

/** `supplierIds` with its `supplier_id` (singular) deep-link fallback — the
 *  dashboard's InReviewWidget links in with exactly one id and no facet-chip
 *  control of its own, so it only ever seeds the facet's initial selection
 *  when the URL carries no `supplierIds` of its own. Mirrors
 *  `DeclarationListV2`'s own `supplierIdsFilter`. */
function supplierIds(params: URLSearchParams): string[] {
  const csv = params.get("supplierIds")
  if (csv) return csv.split(",").filter(Boolean)
  const deepLinkId = params.get("supplier_id")
  return deepLinkId ? [deepLinkId] : []
}

/**
 * Builds the EXACT params object `DeclarationListV2` sends to
 * `useDeclarationsWithPolling` — off the raw URL, not off that component's
 * own derived state, so every caller (the overview list, the review page, a
 * "next declaration" jump) reads the SAME list a reviewer chose, without
 * reimplementing the filter/sort rules themselves.
 *
 * Absent sortBy/sortDir is the DEFAULT, which is "Eingegangen · Neueste
 * zuerst" ON THE WIRE (product-owner call, 27.08.): the endpoint's own
 * no-sort ordering is `ORDER BY id`, i.e. insertion order oldest-first — a
 * fresh upload would land on the LAST row. A direction without a (valid) key
 * is meaningless, so a stale `?sortBy=removed&sortDir=asc` link falls back to
 * the full default, not to "received_at asc".
 */
export function listParamsFromSearch(
  params: URLSearchParams,
): ListDeclarationsParams {
  const sortBy = sanitizedSortBy(params.get("sortBy"))
  const sortDir = sanitizedSortDir(params.get("sortDir"))
  const ids = supplierIds(params)
  return {
    lifecycle: csvParam(params, "lifecycle"),
    supplier: params.get("supplier") || undefined,
    quality: params.get("quality") || undefined,
    supplierIds: ids.length > 0 ? ids.join(",") : undefined,
    docTypes: csvParam(params, "docTypes"),
    frameworks: csvParam(params, "frameworks"),
    // The inbox leaves held copies out unless the reviewer asked for them alone.
    held: params.get("held") === "only" ? "only" : "exclude",
    sortBy: sortBy ?? DATE_SORT_KEY,
    sortDir: sortBy ? sortDir : "desc",
  }
}

/** `?k=v&...` over ONLY the list's own keys, ready to append to a navigate
 *  target — so a row click into review, and every "next declaration" jump
 *  back out of it, carries the exact filters/sort the reviewer chose on the
 *  overview, and nothing else (a studio-only param like `sfw`/`stmt` is never
 *  a list param and is dropped here on purpose). Empty string when the URL
 *  carries none of them, never a bare trailing "?". */
export function listSearchString(params: URLSearchParams): string {
  const out = new URLSearchParams()
  for (const key of DECLARATION_LIST_PARAM_KEYS) {
    const value = params.get(key)
    if (value !== null) out.set(key, value)
  }
  const qs = out.toString()
  return qs ? `?${qs}` : ""
}
